import {
  BarElement,
  CategoryScale,
  Chart,
  Colors,
  LinearScale,
} from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Button, Form, Join } from "react-daisyui";
import { types } from "../../graphql";
import useAutoRetrievePagedQuery from "../../hooks/useAutoRetrievingInfiniteQuery";
import { BlockProps } from "../../containers/Block";
import { flattenInfiniteData } from "../../utils/infiniteQuery";
import Loader from "../Loader";
Chart.register(Colors);
Chart.register(CategoryScale);
Chart.register(LinearScale);
Chart.register(BarElement);

type PollOptions = {
  choiceTTL: number;
  availableChoices: [string];
  allowFreeformEmojiOption: boolean;
};

type PollInteractionData = {
  choice: string;
};

const toInteractionModel = (interaction?: types.Interaction) => ({
  ...interaction,
  data: (interaction?.data?.trim()
    ? JSON.parse(interaction.data)
    : null) as PollInteractionData,
});

export default function Poll({
  zone,
  block,
  query,
  addInteractionMutation,
}: BlockProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const autoPagedQuery = useAutoRetrievePagedQuery(query);

  // Parse the options and interactions to types
  const options = JSON.parse(block.options ?? "") as PollOptions;

  const allInteractions = useMemo(
    () =>
      flattenInfiniteData(query.data, (p) => p.interactions?.items).map(
        toInteractionModel,
      ),
    [query.data],
  );

  useEffect(() => {
    const participantVote = allInteractions.find(
      (i) => i.participantId === zone.participation?.participantId,
    );
    if (participantVote) {
      setSelectedOption(participantVote.data.choice);
    }
  }, [allInteractions, zone.participation?.participantId]);

  const votes = options.availableChoices.map((choice) => ({
    choice,
    votes: allInteractions.filter(
      (interaction) => interaction.data?.choice === choice,
    ),
  }));

  return (
    <div>
      {autoPagedQuery.isFetchingNextPage ? (
        <div>
          <Loader></Loader>
        </div>
      ) : (
        <Bar
          data={{
            labels: votes.map((v) => v.choice),
            datasets: [
              { label: "# of Votes", data: votes.map((v) => v.votes?.length) },
            ],
          }}
          options={{ responsive: true }}
        ></Bar>
      )}
      You selected {selectedOption}
      <Form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Join>
          {options.availableChoices.map((option) => (
            <Button
              value={option}
              key={option}
              className={
                option === selectedOption ? "btn-primary" : "btn-ghost"
              }
              onClick={(e) => {
                addInteractionMutation.mutate({
                  blockId: block.blockId,
                  interactionKey: zone.participation?.participantId,
                  data: JSON.stringify({ choice: option }),
                  participantId: zone.participation?.participantId,
                  timestamp: new Date().getTime(),
                  zoneId: block.zoneId,
                } as types.AddInteractionMutationVariables);
              }}
            >
              {option}
            </Button>
          ))}
        </Join>
      </Form>
    </div>
  );
}
